import { createFocusTrap } from "focus-trap";
import { add, listen, qs, qsa, remove } from "@fluorescent/dom";
import { disableBodyScroll, enableBodyScroll } from "body-scroll-lock";
import Delegate from "ftdomdelegate";

import { on } from "@/glow/events";
import { wrapIframes, wrapTables } from "@/lib/rte";
import {
  initQuickAddQuantitySelectors,
  // initQuickAddModalFooter, /* Removing until quick add modal can be fleshed out. */
} from "@/lib/quick-add";

const classes = {
  visible: "is-visible",
  active: "active",
  fixed: "is-fixed",
};

const selectors = {
  closeBtn: "[data-modal-close]",
  wash: ".modal__wash",
  modalContent: ".modal__content",
  modalInner: ".modal__inner",
  quickAddVariantWrapper: ".product-item__quick-add-wrapper",
};

const modal = node => {
  const focusTrap = createFocusTrap(node, { allowOutsideClick: true });
  const modalInner = qs(selectors.modalInner, node);
  const modalContent = qs(selectors.modalContent, node);

  const delegate = new Delegate(document);

  delegate.on("click", selectors.wash, () => _close());

  const events = [
    listen(qs(selectors.closeBtn, node), "click", e => {
      e.preventDefault();
      _close();
    }),
    listen(node, "keydown", ({ keyCode }) => {
      if (keyCode === 27) _close();
    }),
    on(
      "modal:open",
      (state, { modalContent, narrow = false, quickAdd = false }) => {
        node.dataset.isNarrow = narrow;
        node.dataset.isQuickAdd = quickAdd;
        _renderModalContent(modalContent, quickAdd);
        _open();
      }
    ),
  ];

  const quickAddEvents = [];
  const quickAddQuantitySelectors = [];
  let quickAddObserver;

  const _renderModalContent = (content, isQuickAdd) => {
    const clonedContent = content.cloneNode(true);
    modalContent.innerHTML = "";
    modalContent.appendChild(clonedContent);
    wrapIframes(qsa("iframe", modalContent));
    wrapTables(qsa("table", modalContent));

    if (isQuickAdd) {
      const quickAddVariantWrappers = qsa(
        selectors.quickAddVariantWrapper,
        modalContent
      );
      quickAddVariantWrappers.forEach(wrapper =>
        quickAddQuantitySelectors.push(initQuickAddQuantitySelectors(wrapper))
      );
      quickAddEvents.push(
        listen(quickAddVariantWrappers, "click", e => {
          add(e.currentTarget, classes.active);
        }),
        listen(quickAddVariantWrappers, "mouseleave", e => {
          remove(e.currentTarget, classes.active);
        })
      );

      // Removing until quick add modal can be fleshed out.
      /* if (modalContent.offsetHeight > modalInner.offsetHeight) {
        quickAddObserver = initQuickAddModalFooter(node);
      } */
    }
  };

  const _open = () => {
    // Due to this component being shared between templates we have to
    // animate around it being fixed to the window
    add(node, classes.active);

    // Lock modal height on open to prevent shifting modal
    modalInner.style.setProperty("height", `${modalInner.offsetHeight}px`);

    focusTrap.activate();
    disableBodyScroll(node, {
      allowTouchMove: el => {
        while (el && el !== document.body) {
          if (el.getAttribute("data-scroll-lock-ignore") !== null) {
            return true;
          }
          el = el.parentNode;
        }
      },
      reserveScrollBarGap: true,
    });
  };

  const _close = () => {
    focusTrap.deactivate();
    remove(node, classes.active);

    // Release modal height on close
    modalInner.style.removeProperty("height");

    enableBodyScroll(node);
    quickAddEvents.forEach(unsubscribe => unsubscribe());
    quickAddQuantitySelectors.forEach(selector => selector.unload());
    quickAddObserver?.unload();

    setTimeout(() => {
      modalContent.innerHTML = "";
    }, 300);
  };

  const unload = () => {
    events.forEach(unsubscribe => unsubscribe());
  };

  return { unload };
};

export default modal;
